import { useRef, type MouseEvent } from "react";
import { isTextToolbarTarget } from "../domain/text-editing";
import { placeEmailBuilderRichTextCaret } from "../editing/richText/emailBuilderRichTextSession";
import { getEditorStoreState } from "../domain/store";
import { shouldDeferChildPointerActivation } from "../domain/selection/targets";

type BlockTarget = {
  rowId: string;
  columnId: string;
  blockId: string;
};

type UseEditableButtonPointerParams = {
  block: { id: string };
  isEditing: boolean;
  selected: boolean;
  textEditReentryGuard: string | null;
  setSelection: (selection: { kind: "block" } & BlockTarget) => void;
  setTextEditReentryGuard: (blockId: string | null) => void;
  target: BlockTarget;
  enterEdit: (at?: { x: number; y: number }) => void;
};

function readInlineButtonEditorElement(blockId: string): HTMLElement | null {
  return document.querySelector(`[data-email-builder-inline-button-label="${blockId}"]`);
}

export function useEditableButtonPointer({
  block,
  isEditing,
  selected,
  setSelection,
  setTextEditReentryGuard,
  target,
  enterEdit,
}: UseEditableButtonPointerParams) {
  const editPointerStartRef = useRef<{ x: number; y: number } | null>(null);

  function handleMouseDown(event: MouseEvent) {
    const editor = readInlineButtonEditorElement(block.id);
    if (isEditing && editor) {
      if (isTextToolbarTarget(event.target)) {
        editPointerStartRef.current = null;
        return;
      }
      if (event.target instanceof Node && editor.contains(event.target)) {
        editPointerStartRef.current = null;
      } else if (event.button === 0) {
        editPointerStartRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
      }
      return;
    }
    if (event.button !== 0) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    if (!selected) {
      const { selection, document } = getEditorStoreState();
      if (
        shouldDeferChildPointerActivation(
          selection,
          { kind: "block", ...target },
          document.sections,
          event,
        )
      ) {
        return;
      }
      setTextEditReentryGuard(null);
      setSelection({ kind: "block", ...target });
      return;
    }
    if (!isEditing) {
      return;
    }
  }

  function handleMouseUp(event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }
    const editor = readInlineButtonEditorElement(block.id);
    if (isEditing && editor) {
      if (isTextToolbarTarget(event.target)) {
        editPointerStartRef.current = null;
        return;
      }
      const hit = event.target;
      const start = editPointerStartRef.current;
      editPointerStartRef.current = null;
      if (start && hit instanceof Node && !editor.contains(hit)) {
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (dx * dx + dy * dy < 64) {
          placeEmailBuilderRichTextCaret(block.id, event.clientX, event.clientY);
        }
      }
      return;
    }
    const start = editPointerStartRef.current;
    editPointerStartRef.current = null;
    if (!start || !selected) {
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (dx * dx + dy * dy < 64) {
      event.stopPropagation();
      enterEdit({ x: event.clientX, y: event.clientY });
    }
  }

  function handleDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    if (isEditing) {
      return;
    }
    setTextEditReentryGuard(null);
    enterEdit({ x: event.clientX, y: event.clientY });
  }

  function handleClick(event: MouseEvent) {
    if (isEditing && !isTextToolbarTarget(event.target)) {
      event.stopPropagation();
    }
  }

  return {
    rootHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onDoubleClick: handleDoubleClick,
      onClick: handleClick,
    },
  };
}
